import React, { useEffect, useMemo, useState } from "react";
import {
  arrayUnion,
  doc,
  getDoc,
  serverTimestamp,
  setDoc,
  updateDoc,
} from "firebase/firestore";
import { getAuth } from "firebase/auth";
import {
  getDownloadURL,
  getStorage,
  ref,
  uploadBytes,
} from "firebase/storage";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { db } from "../firebaseConfig";
import "./AddItem.css";

const makeId = () =>
  `${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;

const normalizeSavedItem = (item = {}, index = 0) => ({
  id: item.id || `legacy_${index}_${makeId()}`,
  text: item.text || "",
  checked: item.checked === true,
  notes: item.notes || "",
  images: Array.isArray(item.images) ? item.images : [],
  createdAt: item.createdAt || null,
  completedAt: item.completedAt || null,
  completedBy: item.completedBy || "",
  syncedToJobNote: item.syncedToJobNote === true,
});

const buildInitialChecklist = (description = "") => {
  const clean = String(description || "").trim();
  if (!clean) return [];

  const lines = clean
    .split(/\n+/)
    .map((line) => line.replace(/^\s*[•*-]\s*/, "").trim())
    .filter(Boolean);

  const source = lines.length > 1
    ? lines
    : clean
        .split(/(?<=[.!?])\s+/)
        .map((part) => part.trim())
        .filter(Boolean);

  return source.map((text) => ({
    id: makeId(),
    text,
    checked: false,
    notes: "",
    images: [],
    createdAt: new Date().toISOString(),
    completedAt: null,
    completedBy: "",
    syncedToJobNote: false,
  }));
};

const formatDateTime = (value) => {
  if (!value) return "";
  if (value?.toDate) return value.toDate().toLocaleString();
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? String(value) : date.toLocaleString();
};

const LoadingSpinner = ({ label = "Saving" }) => (
  <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
    <style>{`@keyframes checklist-spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    <span
      aria-hidden="true"
      style={{
        width: 14,
        height: 14,
        border: "2px solid rgba(255,255,255,.65)",
        borderTopColor: "#111827",
        borderRadius: "50%",
        display: "inline-block",
        animation: "checklist-spin .8s linear infinite",
      }}
    />
    {label}
  </span>
);

const JobChecklist = () => {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [jobData, setJobData] = useState(null);
  const [checklist, setChecklist] = useState([]);
  const [createdBy, setCreatedBy] = useState("User");
  const [newPunchText, setNewPunchText] = useState("");
  const [saving, setSaving] = useState(false);
  const [uploadingItemId, setUploadingItemId] = useState(null);
  const [message, setMessage] = useState("");
  const [showCompleted, setShowCompleted] = useState(true);
  const [previewImage, setPreviewImage] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        const auth = getAuth();
        const currentUser = auth.currentUser;

        if (currentUser) {
          const userSnap = await getDoc(doc(db, "users", currentUser.uid));
          if (userSnap.exists()) {
            const user = userSnap.data();
            setCreatedBy(
              `${user.firstName || ""} ${user.lastName || ""}`.trim() ||
                user.displayName ||
                user.email ||
                "User"
            );
          }
        }

        const jobSnap = await getDoc(doc(db, "jobs", jobId));
        if (!jobSnap.exists()) {
          setMessage("Job not found.");
          return;
        }

        const job = { id: jobSnap.id, ...jobSnap.data() };
        setJobData(job);

        const checklistSnap = await getDoc(doc(db, "jobChecklist", jobId));
        if (checklistSnap.exists()) {
          const saved = checklistSnap.data();
          setChecklist(
            Array.isArray(saved.items)
              ? saved.items.map(normalizeSavedItem)
              : []
          );
        } else {
          setChecklist(buildInitialChecklist(job.jobDescription || job.scope || ""));
        }
      } catch (error) {
        console.error("Error loading checklist:", error);
        setMessage("There was a problem loading this checklist.");
      }
    };

    load();
  }, [jobId]);


  const handleBackToJobDetails = () => {
    const returnTo = location.state?.returnTo;

    if (returnTo) {
      navigate(returnTo, {
        state: {
          reopenJobId: location.state?.reopenJobId || jobId,
          reopenOccurrenceDate: location.state?.reopenOccurrenceDate || "",
        },
      });
      return;
    }

    navigate(-1);
  };

  const stats = useMemo(() => {
    const total = checklist.length;
    const completed = checklist.filter((item) => item.checked).length;
    return {
      total,
      completed,
      open: total - completed,
      percent: total ? Math.round((completed / total) * 100) : 0,
    };
  }, [checklist]);

  const visibleChecklist = useMemo(
    () => checklist.filter((item) => showCompleted || !item.checked),
    [checklist, showCompleted]
  );

  const patchItem = (itemId, patch) => {
    setChecklist((prev) =>
      prev.map((item) => (item.id === itemId ? { ...item, ...patch } : item))
    );
  };

  const addPunchItem = () => {
    const text = newPunchText.trim();
    if (!text) return;

    setChecklist((prev) => [
      ...prev,
      {
        id: makeId(),
        text,
        checked: false,
        notes: "",
        images: [],
        createdAt: new Date().toISOString(),
        completedAt: null,
        completedBy: "",
        syncedToJobNote: false,
      },
    ]);
    setNewPunchText("");
  };

  const deleteItem = (itemId) => {
    if (!window.confirm("Remove this checklist / punch-list item?")) return;
    setChecklist((prev) => prev.filter((item) => item.id !== itemId));
  };

  const toggleCompleted = (itemId) => {
    setChecklist((prev) =>
      prev.map((item) => {
        if (item.id !== itemId) return item;
        const nextChecked = !item.checked;
        return {
          ...item,
          checked: nextChecked,
          completedAt: nextChecked ? new Date().toISOString() : null,
          completedBy: nextChecked ? createdBy : "",
          // Reopening allows a later completion to create a fresh completion note.
          syncedToJobNote: nextChecked ? item.syncedToJobNote === true : false,
        };
      })
    );
  };

  const uploadItemImages = async (itemId, files) => {
    const selected = Array.from(files || []);
    if (!selected.length) return;

    setUploadingItemId(itemId);
    setMessage("");

    try {
      const storage = getStorage();
      const uploaded = await Promise.all(
        selected.map(async (file) => {
          const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, "_");
          const fileRef = ref(
            storage,
            `jobChecklist/${jobId}/${itemId}/${Date.now()}_${safeName}`
          );
          await uploadBytes(fileRef, file);
          const fileURL = await getDownloadURL(fileRef);
          return {
            fileURL,
            fileName: file.name,
            uploadedAt: new Date().toISOString(),
            uploadedBy: createdBy,
          };
        })
      );

      setChecklist((prev) =>
        prev.map((item) =>
          item.id === itemId
            ? { ...item, images: [...(item.images || []), ...uploaded] }
            : item
        )
      );
    } catch (error) {
      console.error("Error uploading checklist images:", error);
      setMessage("Image upload failed. Please try again.");
    } finally {
      setUploadingItemId(null);
    }
  };

  const removeItemImage = (itemId, imageIndex) => {
    setChecklist((prev) =>
      prev.map((item) =>
        item.id === itemId
          ? {
              ...item,
              images: (item.images || []).filter((_, index) => index !== imageIndex),
            }
          : item
      )
    );
  };

  const buildCompletionNoteText = (items) => {
    const header = `Checklist Completion Update${jobData?.wo ? ` - WO #${jobData.wo}` : ""}`;
    const lines = items.map((item) => {
      const detail = item.notes?.trim() ? ` — ${item.notes.trim()}` : "";
      return `✓ ${item.text}${detail}`;
    });

    return `${header}\n${lines.join("\n")}`;
  };

  const saveChecklist = async () => {
    if (!jobData) return;
    setSaving(true);
    setMessage("");

    try {
      const newlyCompleted = checklist.filter(
        (item) => item.checked && !item.syncedToJobNote
      );

      let itemsToSave = checklist;

      if (newlyCompleted.length > 0) {
        const completionNote = {
          text: buildCompletionNoteText(newlyCompleted),
          timestamp: new Date().toLocaleString(),
          author: createdBy || "User",
          files: [],
          noteType: "checklist",
          checklistId: jobId,
          checklistItemIds: newlyCompleted.map((item) => item.id),
        };

        await updateDoc(doc(db, "jobs", jobId), {
          notes: arrayUnion(completionNote),
        });

        const syncedIds = new Set(newlyCompleted.map((item) => item.id));
        itemsToSave = checklist.map((item) =>
          syncedIds.has(item.id) ? { ...item, syncedToJobNote: true } : item
        );
        setChecklist(itemsToSave);
      }

      await setDoc(
        doc(db, "jobChecklist", jobId),
        {
          jobId,
          siteName: jobData.siteName || "",
          wo: jobData.wo || "",
          scope: jobData.scope || "",
          items: itemsToSave,
          stats: {
            total: itemsToSave.length,
            completed: itemsToSave.filter((item) => item.checked).length,
          },
          lastUpdated: serverTimestamp(),
          lastUpdatedBy: createdBy || "User",
        },
        { merge: true }
      );

      setMessage(
        newlyCompleted.length
          ? `Checklist saved. ${newlyCompleted.length} completed item${newlyCompleted.length === 1 ? " was" : "s were"} added to the job notes.`
          : "Checklist saved."
      );
    } catch (error) {
      console.error("Error saving checklist:", error);
      setMessage("There was an error saving the checklist.");
    } finally {
      setSaving(false);
    }
  };

  const downloadReport = () => {
    if (!jobData) return;

    const escapeHtml = (value = "") =>
      String(value)
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#039;");

    const itemHtml = checklist
      .map(
        (item, index) => `
          <section class="item ${item.checked ? "done" : "open"}">
            <div class="item-head">
              <span class="mark">${item.checked ? "✓" : ""}</span>
              <div>
                <div class="task">${index + 1}. ${escapeHtml(item.text)}</div>
                <div class="status">${item.checked ? "Completed" : "Open"}${
                  item.completedBy ? ` by ${escapeHtml(item.completedBy)}` : ""
                }${item.completedAt ? ` on ${escapeHtml(formatDateTime(item.completedAt))}` : ""}</div>
              </div>
            </div>
            ${item.notes ? `<div class="notes"><strong>Notes:</strong> ${escapeHtml(item.notes)}</div>` : ""}
            ${
              item.images?.length
                ? `<div class="images">${item.images
                    .map(
                      (image) =>
                        `<img src="${escapeHtml(image.fileURL)}" alt="${escapeHtml(image.fileName || "Checklist image")}" />`
                    )
                    .join("")}</div>`
                : ""
            }
          </section>`
      )
      .join("");

    const html = `<!doctype html>
<html>
<head>
<meta charset="utf-8" />
<title>Checklist Report - WO ${escapeHtml(jobData.wo || "")}</title>
<style>
  body { font-family: Arial, sans-serif; color:#172033; margin:32px; }
  h1 { margin:0 0 6px; }
  .meta { color:#526071; margin-bottom:22px; }
  .summary { display:flex; gap:12px; margin:18px 0 24px; }
  .pill { border:1px solid #ccd5e0; border-radius:999px; padding:7px 12px; font-weight:700; }
  .item { border:1px solid #d8e0e8; border-radius:10px; padding:14px; margin-bottom:14px; page-break-inside:avoid; }
  .done { background:#f1fbf4; border-color:#a7d9b5; }
  .item-head { display:flex; gap:10px; align-items:flex-start; }
  .mark { width:22px; height:22px; border:2px solid #718096; border-radius:5px; display:inline-flex; align-items:center; justify-content:center; font-weight:900; color:#fff; background:#fff; flex:0 0 auto; }
  .done .mark { background:#1f9d55; border-color:#1f9d55; }
  .task { font-weight:700; line-height:1.4; }
  .status { font-size:12px; color:#667085; margin-top:3px; }
  .notes { margin:10px 0 0 32px; white-space:pre-wrap; }
  .images { margin:12px 0 0 32px; display:grid; grid-template-columns:repeat(2, minmax(0,1fr)); gap:10px; }
  .images img { width:100%; max-height:300px; object-fit:contain; border:1px solid #d8e0e8; border-radius:8px; }
  @media print { body { margin:18px; } }
</style>
</head>
<body>
  <h1>Job Checklist / Punch List Report</h1>
  <div class="meta">
    <strong>WO:</strong> ${escapeHtml(jobData.wo || "N/A")} &nbsp; | &nbsp;
    <strong>Site:</strong> ${escapeHtml(jobData.siteName || "N/A")}<br/>
    <strong>Scope:</strong> ${escapeHtml(jobData.scope || "")}
  </div>
  <div class="summary">
    <span class="pill">Total: ${stats.total}</span>
    <span class="pill">Completed: ${stats.completed}</span>
    <span class="pill">Open: ${stats.open}</span>
    <span class="pill">Progress: ${stats.percent}%</span>
  </div>
  ${itemHtml || "<p>No checklist items.</p>"}
</body>
</html>`;

    const blob = new Blob([html], { type: "text/html;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = `Checklist_WO_${jobData.wo || jobId}.html`;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    URL.revokeObjectURL(url);
  };

  if (!jobData) {
    return (
      <div className="add-item-page" style={{ padding: 24 }}>
        <h1>Job Checklist</h1>
        <p>{message || "Loading job data..."}</p>
      </div>
    );
  }

  return (
    <div className="add-item-page" style={{ padding: "20px", background: "#f5f7fb", minHeight: "100vh" }}>
      <div style={{ maxWidth: 1100, margin: "0 auto" }}>
        <div
          style={{
            background: "#fff",
            border: "1px solid #dce3ec",
            borderRadius: 16,
            padding: 20,
            boxShadow: "0 8px 24px rgba(15, 23, 42, .06)",
            marginBottom: 16,
          }}
        >
          <div style={{ display: "flex", justifyContent: "space-between", gap: 16, flexWrap: "wrap" }}>
            <div>
              <h1 style={{ margin: 0, color: "#162033" }}>Job Checklist / Punch List</h1>
              <div style={{ marginTop: 6, color: "#667085" }}>
                WO #{jobData.wo || "N/A"} · {jobData.siteName || "No site name"}
              </div>
              {jobData.scope && (
                <div style={{ marginTop: 8, fontWeight: 700, color: "#344054" }}>
                  {jobData.scope}
                </div>
              )}
            </div>

            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "flex-start" }}>
              <button
                type="button"
                onClick={handleBackToJobDetails}
                style={{ ...secondaryButton, background: "#1d4ed8", color: "#fff", borderColor: "#1d4ed8" }}
              >
                ← Back to Job Details
              </button>
              <button
                type="button"
                onClick={() => setShowCompleted((prev) => !prev)}
                style={secondaryButton}
              >
                {showCompleted ? "Hide Completed" : "Show Completed"}
              </button>
              <button type="button" onClick={downloadReport} style={secondaryButton}>
                Download Report
              </button>
            </div>
          </div>

          <div style={{ marginTop: 18 }}>
            <div style={{ height: 10, background: "#e8edf3", borderRadius: 999, overflow: "hidden" }}>
              <div
                style={{
                  width: `${stats.percent}%`,
                  height: "100%",
                  background: "#2f9e64",
                  transition: "width .2s ease",
                }}
              />
            </div>
            <div style={{ display: "flex", gap: 14, flexWrap: "wrap", marginTop: 8, fontSize: 13, color: "#526071" }}>
              <strong>{stats.percent}% complete</strong>
              <span>{stats.total} total</span>
              <span>{stats.completed} completed</span>
              <span>{stats.open} open</span>
            </div>
          </div>
        </div>

        <div
          style={{
            background: "#fff",
            border: "1px solid #dce3ec",
            borderRadius: 16,
            padding: 16,
            marginBottom: 16,
          }}
        >
          <div style={{ fontWeight: 800, marginBottom: 8 }}>Add Punch-List Item</div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <input
              value={newPunchText}
              onChange={(e) => setNewPunchText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") addPunchItem();
              }}
              placeholder="Example: Touch up paint at living room window"
              style={{
                flex: "1 1 560px",
                minWidth: 240,
                padding: "11px 12px",
                border: "1px solid #cfd8e3",
                borderRadius: 10,
                fontSize: 15,
              }}
            />
            <button type="button" onClick={addPunchItem} style={primaryButton}>
              + Add Item
            </button>
          </div>
        </div>

        <div style={{ display: "grid", gap: 12 }}>
          {visibleChecklist.map((item, visibleIndex) => {
            const actualIndex = checklist.findIndex((entry) => entry.id === item.id);
            return (
              <div
                key={item.id}
                style={{
                  background: item.checked ? "#eef9f1" : "#fff",
                  border: item.checked ? "1px solid #9dd3ad" : "1px solid #dce3ec",
                  borderRadius: 14,
                  padding: 14,
                  boxShadow: "0 4px 14px rgba(15, 23, 42, .04)",
                }}
              >
                <div style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
                  <button
                    type="button"
                    onClick={() => toggleCompleted(item.id)}
                    aria-label={item.checked ? "Mark item open" : "Mark item completed"}
                    title={item.checked ? "Mark open" : "Mark completed"}
                    style={{
                      width: 23,
                      height: 23,
                      minWidth: 23,
                      padding: 0,
                      marginTop: 2,
                      borderRadius: 5,
                      border: item.checked ? "2px solid #238b55" : "2px solid #8290a3",
                      background: item.checked ? "#238b55" : "#fff",
                      color: "#fff",
                      fontSize: 16,
                      fontWeight: 900,
                      lineHeight: 1,
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      cursor: "pointer",
                    }}
                  >
                    {item.checked ? "✓" : ""}
                  </button>

                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "flex-start" }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 12, color: "#7b8797", marginBottom: 3 }}>
                          Item {actualIndex + 1}
                        </div>
                        <textarea
                          value={item.text}
                          onChange={(e) => patchItem(item.id, { text: e.target.value })}
                          rows={Math.max(1, Math.min(4, Math.ceil((item.text?.length || 0) / 90)))}
                          style={{
                            width: "100%",
                            resize: "vertical",
                            border: "none",
                            borderBottom: "1px solid transparent",
                            outline: "none",
                            background: "transparent",
                            fontWeight: 800,
                            fontSize: 15,
                            lineHeight: 1.45,
                            color: "#172033",
                            padding: "2px 0 6px",
                          }}
                        />
                      </div>
                      <button type="button" onClick={() => deleteItem(item.id)} style={deleteButton}>
                        Remove
                      </button>
                    </div>

                    <div style={{ marginTop: 10 }}>
                      <label style={{ display: "block", fontSize: 12, fontWeight: 800, color: "#526071", marginBottom: 5 }}>
                        Punch-list notes / what needs to be done
                      </label>
                      <textarea
                        value={item.notes || ""}
                        onChange={(e) => patchItem(item.id, { notes: e.target.value })}
                        placeholder="Add details, correction needed, location, materials, follow-up instructions, etc."
                        rows={3}
                        style={{
                          width: "100%",
                          boxSizing: "border-box",
                          padding: 10,
                          border: "1px solid #cfd8e3",
                          borderRadius: 9,
                          resize: "vertical",
                          fontSize: 14,
                        }}
                      />
                    </div>

                    <div style={{ marginTop: 10, display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
                      <label
                        style={{
                          ...secondaryButton,
                          display: "inline-flex",
                          alignItems: "center",
                          cursor: uploadingItemId === item.id ? "not-allowed" : "pointer",
                          opacity: uploadingItemId === item.id ? 0.7 : 1,
                        }}
                      >
                        {uploadingItemId === item.id ? <LoadingSpinner label="Uploading" /> : "+ Add Images"}
                        <input
                          type="file"
                          accept="image/*"
                          multiple
                          disabled={uploadingItemId === item.id}
                          onChange={(e) => {
                            uploadItemImages(item.id, e.target.files);
                            e.target.value = "";
                          }}
                          style={{ display: "none" }}
                        />
                      </label>

                      {item.checked && (
                        <span style={{ fontSize: 12, color: "#238b55", fontWeight: 800 }}>
                          ✓ Completed{item.completedBy ? ` by ${item.completedBy}` : ""}
                          {item.completedAt ? ` · ${formatDateTime(item.completedAt)}` : ""}
                        </span>
                      )}
                    </div>

                    {item.images?.length > 0 && (
                      <div
                        style={{
                          display: "grid",
                          gridTemplateColumns: "repeat(auto-fill, minmax(130px, 1fr))",
                          gap: 10,
                          marginTop: 12,
                        }}
                      >
                        {item.images.map((image, imageIndex) => (
                          <div key={`${image.fileURL}_${imageIndex}`} style={{ position: "relative" }}>
                            <button
                              type="button"
                              onClick={() => setPreviewImage(image.fileURL)}
                              style={{ padding: 0, border: "none", background: "none", width: "100%", cursor: "zoom-in" }}
                            >
                              <img
                                src={image.fileURL}
                                alt={image.fileName || "Checklist"}
                                style={{
                                  width: "100%",
                                  height: 120,
                                  objectFit: "cover",
                                  borderRadius: 9,
                                  border: "1px solid #d6dee8",
                                  display: "block",
                                }}
                              />
                            </button>
                            <button
                              type="button"
                              onClick={() => removeItemImage(item.id, imageIndex)}
                              title="Remove image from checklist"
                              style={{
                                position: "absolute",
                                top: 5,
                                right: 5,
                                width: 24,
                                height: 24,
                                borderRadius: "50%",
                                border: "none",
                                background: "rgba(17,24,39,.82)",
                                color: "#fff",
                                cursor: "pointer",
                                fontWeight: 900,
                              }}
                            >
                              ×
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}

          {visibleChecklist.length === 0 && (
            <div style={{ background: "#fff", border: "1px solid #dce3ec", borderRadius: 14, padding: 24, textAlign: "center", color: "#667085" }}>
              {checklist.length ? "All completed items are hidden." : "No checklist items yet. Add your first punch-list item above."}
            </div>
          )}
        </div>

        <div
          style={{
            position: "sticky",
            bottom: 12,
            marginTop: 18,
            background: "rgba(255,255,255,.96)",
            border: "1px solid #dce3ec",
            borderRadius: 14,
            padding: 12,
            boxShadow: "0 10px 25px rgba(15,23,42,.12)",
            display: "flex",
            justifyContent: "space-between",
            gap: 12,
            alignItems: "center",
            flexWrap: "wrap",
          }}
        >
          <div style={{ minHeight: 20, color: message.includes("error") || message.includes("problem") || message.includes("failed") ? "#b42318" : "#2f6d49", fontWeight: 700 }}>
            {message}
          </div>
          <button type="button" onClick={saveChecklist} disabled={saving} style={{ ...primaryButton, minWidth: 150, opacity: saving ? 0.75 : 1 }}>
            {saving ? <LoadingSpinner label="Saving" /> : "Save Checklist"}
          </button>
        </div>
      </div>

      {previewImage && (
        <div
          role="dialog"
          aria-modal="true"
          onClick={() => setPreviewImage(null)}
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,.82)",
            zIndex: 99999,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 24,
          }}
        >
          <img
            src={previewImage}
            alt="Checklist preview"
            onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: "95vw", maxHeight: "92vh", objectFit: "contain", borderRadius: 10 }}
          />
          <button
            type="button"
            onClick={() => setPreviewImage(null)}
            style={{ position: "fixed", top: 18, right: 18, width: 40, height: 40, borderRadius: "50%", border: "none", fontSize: 24, cursor: "pointer" }}
          >
            ×
          </button>
        </div>
      )}
    </div>
  );
};

const primaryButton = {
  border: "1px solid #176b45",
  background: "#238b55",
  color: "#fff",
  padding: "10px 14px",
  borderRadius: 9,
  fontWeight: 800,
  cursor: "pointer",
};

const secondaryButton = {
  border: "1px solid #c7d2df",
  background: "#f8fafc",
  color: "#263447",
  padding: "9px 12px",
  borderRadius: 9,
  fontWeight: 800,
  cursor: "pointer",
};

const deleteButton = {
  border: "1px solid #efc4c4",
  background: "#fff5f5",
  color: "#a72b2b",
  padding: "6px 9px",
  borderRadius: 8,
  fontWeight: 800,
  cursor: "pointer",
  fontSize: 12,
};

export default JobChecklist;
